import React, { createContext, useContext } from 'react';
import { Link, useLocation } from 'react-router-dom';
import logoFace from '../../assets/logo-face.png';

// The mobile account control, and the channel AppLayout uses to hand it to
// whichever header is rendering.
//
// WHY A CONTEXT AND NOT A PROP
// The control has to sit vertically centred on the TITLE, and only the header
// knows how tall its title line is — it changes with the breakpoint and again
// when the header condenses on scroll. AppLayout previously pinned the control
// to the top of the whole header block, which put it level with the title only
// by coincidence and drifted the moment a subtitle appeared.
//
// Handing it down instead lets PageHeader place it inside the title row, where
// `top-1/2 -translate-y-1/2` centres it on the actual line box at any size,
// with no magic numbers to keep in sync.
//
// A page that composes its own header (VaultEntry's back button, GlowupDetail's
// kebab) simply does not read the context and does not get the control. That is
// the right outcome: those surfaces already own their top-right corner, and the
// old always-on absolute control was landing on top of GlowupDetail's kebab.

export const HeaderAccountContext = createContext(null);

// Carries the brand crest rather than a generic user glyph — this is the
// member's own avatar slot, and the Account page uses the same mark on the same
// ringed disc, so tapping it lands somewhere that looks like what you tapped.
// 44px is the minimum comfortable tap target.
//
// `mix-blend-screen` drops the crest's black plate onto the disc fill, matching
// AppShellSidebar and the Account avatar.
export const HeaderAccountButton = ({ item, className = '' }) => {
  const location = useLocation();
  const active = location.pathname === item.to;
  return (
    <Link
      to={item.to}
      aria-label={item.label}
      aria-current={active ? 'page' : undefined}
      data-testid="header-account"
      className={[
        'flex items-center justify-center w-11 h-11 rounded-full border overflow-hidden',
        'bg-[#232322] transition-colors duration-200 ease-mogged',
        active ? 'border-aurum' : 'border-[rgba(201,164,92,0.4)]',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <img
        src={logoFace}
        alt=""
        aria-hidden="true"
        className="w-[30px] h-[30px] object-contain mix-blend-screen"
      />
    </Link>
  );
};

// Renders the control centred on its parent's height, or nothing when no
// account item was provided. The parent must be `position: relative`.
export const HeaderAccountSlot = () => {
  const item = useContext(HeaderAccountContext);
  if (!item) return null;
  return (
    <HeaderAccountButton
      item={item}
      className="lg:hidden absolute right-0 top-1/2 -translate-y-1/2 z-10"
    />
  );
};
